import type React from 'react';
import {useState} from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, Stack, TextField,
} from '@mui/material';
import {errorText} from '../../hooks/notices';
import type {ClauseInput, PriceClause, PriceMonitor} from '../../types/monitorTypes';

type Props = {
    options: PriceMonitor['options'];
    clause: PriceClause | null;
    onSubmit: (input: ClauseInput) => Promise<unknown>;
    onClose: () => void;
};

type Draft = Omit<ClauseInput, 'contractId' | 'seriesId' | 'sharePct' | 'capPct' | 'floorPct' | 'triggerPct' | 'announcedPct'> & {
    contractId: number | ''; seriesId: number | ''; sharePct: string; capPct: string; floorPct: string;
    triggerPct: string; announcedPct: string;
};

const num = (v: string) => (v.trim() === '' ? null : Number(v));
const str = (v: number | null) => (v === null ? '' : String(v));

/** Add or edit the index-linked price clause of a contract in this category. */
export const ClauseDialog: React.FC<Props> = ({options, clause, onSubmit, onClose}) => {
    const [draft, setDraft] = useState<Draft>(() => ({
        contractId: clause?.contract.id ?? options.contracts[0]?.id ?? '',
        seriesId: clause?.series.id ?? '',
        baseMonth: clause?.baseMonth.slice(0, 7) ?? '',
        sharePct: str(clause?.sharePct ?? 100),
        revision: clause?.revision ?? 'annual',
        nextRevision: clause?.nextRevision ?? '',
        capPct: str(clause?.capPct ?? null), floorPct: str(clause?.floorPct ?? null),
        triggerPct: str(clause?.triggerPct ?? null), announcedPct: str(clause?.announcedPct ?? null),
        note: clause?.note ?? '',
    }));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({...draft, [k]: v});
    const contract = options.contracts.find(c => c.id === draft.contractId);
    const ready = draft.contractId !== '' && draft.seriesId !== '' && /^\d{4}-\d{2}$/.test(draft.baseMonth);

    const submit = () => {
        if (!ready) return;
        setBusy(true);
        setError(null);
        onSubmit({
            contractId: Number(draft.contractId), seriesId: Number(draft.seriesId), baseMonth: `${draft.baseMonth}-01`,
            sharePct: num(draft.sharePct) ?? 100, revision: draft.revision, nextRevision: draft.nextRevision || null,
            capPct: num(draft.capPct), floorPct: num(draft.floorPct), triggerPct: num(draft.triggerPct),
            announcedPct: num(draft.announcedPct), note: draft.note,
        }).then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };
    const pct = (label: string, k: 'sharePct' | 'capPct' | 'floorPct' | 'triggerPct' | 'announcedPct', help: string) => (
        <TextField label={label} size="small" type="number" value={draft[k]} onChange={e => set(k, e.target.value)}
                   helperText={help} sx={{flex: 1}}
                   slotProps={{input: {endAdornment: <InputAdornment position="end">%</InputAdornment>}}}/>
    );

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-clause-title">
            <DialogTitle id="cp-clause-title">{clause ? 'Edit price clause' : 'Add price clause'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField select label="Contract" size="small" value={draft.contractId} disabled={Boolean(clause)}
                               onChange={e => set('contractId', Number(e.target.value))}>
                        {options.contracts.map(c => (
                            <MenuItem key={c.id} value={c.id}>{`${c.supplier || 'Unknown supplier'} · ${c.title || `Contract ${c.id}`}`}</MenuItem>
                        ))}
                    </TextField>
                    {contract?.priceAdjustment && (
                        <Alert severity="info" icon={false}>{`From the contract: “${contract.priceAdjustment}”`}</Alert>
                    )}
                    <TextField select label="Index reference" size="small" value={draft.seriesId}
                               onChange={e => set('seriesId', Number(e.target.value))}>
                        {options.series.map(s => <MenuItem key={s.id} value={s.id}>{`${s.name}${s.unit ? ` (${s.unit})` : ''}`}</MenuItem>)}
                    </TextField>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Base month" size="small" type="month" value={draft.baseMonth} sx={{flex: 1}}
                                   onChange={e => set('baseMonth', e.target.value)} slotProps={{inputLabel: {shrink: true}}}
                                   helperText="Index month the current price is based on"/>
                        {pct('Indexed share', 'sharePct', 'Share of the price that follows the index')}
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField select label="Revision" size="small" value={draft.revision} sx={{flex: 1}}
                                   onChange={e => set('revision', e.target.value as Draft['revision'])}>
                            {options.revisions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                        </TextField>
                        <TextField label="Next revision" size="small" type="date" value={draft.nextRevision ?? ''} sx={{flex: 1}}
                                   onChange={e => set('nextRevision', e.target.value)} slotProps={{inputLabel: {shrink: true}}}/>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        {pct('Cap', 'capPct', 'Largest increase')}
                        {pct('Floor', 'floorPct', 'Largest decrease')}
                        {pct('Trigger', 'triggerPct', 'Index move before a change')}
                    </Stack>
                    {pct('Announced increase', 'announcedPct', 'What the supplier announced for the next window, if anything')}
                    <TextField label="Note" size="small" multiline minRows={2} value={draft.note}
                               onChange={e => set('note', e.target.value)} slotProps={{htmlInput: {maxLength: 2000}}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !ready}>{clause ? 'Save' : 'Add clause'}</Button>
            </DialogActions>
        </Dialog>
    );
};
